import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { firstValueFrom, Observable, of, tap, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { IPhoneRule, IPhoneValidationRequest, IPhoneValidationResponse } from '../../interfaces/phone-validation.interface';

@Injectable({
  providedIn: 'root'
})
export class PhoneValidationService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/phones`;
  private readonly CACHE_KEY = 'phone_rules_cache';
  private readonly CACHE_DURATION = 2 * 60 * 60 * 1000; // 2 horas en milisegundos

  private readonly rulesSignal = signal<IPhoneRule[]>([]);
  private loadPromise: Promise<IPhoneRule[]> | null = null;

  /**
   * Precarga las reglas en memoria (signal). Idempotente: si ya están cargadas no hace nada.
   * Pensado para llamarse desde APP_INITIALIZER.
   */
  loadRules(): Promise<IPhoneRule[]> {
    if (this.rulesSignal().length > 0) {
      return Promise.resolve(this.rulesSignal());
    }
    if (this.loadPromise) {
      return this.loadPromise;
    }
    this.loadPromise = firstValueFrom(this.getAllPhoneRules())
      .then(rules => {
        this.rulesSignal.set(rules);
        return rules;
      })
      .catch(() => {
        this.loadPromise = null;
        return [] as IPhoneRule[];
      });
    return this.loadPromise;
  }

  /**
   * Reglas actualmente disponibles en memoria. Vacío si aún no se cargaron.
   */
  getRulesSync(): IPhoneRule[] {
    return this.rulesSignal();
  }

  /**
   * Obtiene todas las reglas de validación de teléfonos por país
   * Las reglas se cachean en localStorage con duración de 2 horas
   */
  getAllPhoneRules(): Observable<IPhoneRule[]> {
    const cached = this.getFromCache();
    if (cached) {
      return of(cached);
    }

    return this.http.get<IPhoneRule[]>(`${this.baseUrl}/rules`).pipe(
      tap(rules => this.saveToCache(rules))
    );
  }

  /**
   * Obtiene las reglas desde localStorage si existen y no han expirado
   */
  private getFromCache(): IPhoneRule[] | null {
    try {
      const cached = localStorage.getItem(this.CACHE_KEY);
      if (!cached) return null;

      const { data, timestamp } = JSON.parse(cached);
      const now = Date.now();

      // Verificar si el caché ha expirado (2 horas)
      if (now - timestamp > this.CACHE_DURATION) {
        localStorage.removeItem(this.CACHE_KEY);
        return null;
      }
      return data;
    } catch (error) {
      console.error('Error al leer caché de reglas telefónicas:', error);
      return null;
    }
  }

  /**
   * Guarda las reglas en localStorage con timestamp actual
   */
  private saveToCache(rules: IPhoneRule[]): void {
    try {
      const cacheData = {
        data: rules,
        timestamp: Date.now()
      };
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error al guardar caché de reglas telefónicas:', error);
    }
  }

  /**
   * Obtiene las reglas de validación para un código de país específico
   * @param countryCode - Código de país display (ej: '+502', '+57', '+52')
   */
  getPhoneRulesByCode(countryCode: string): Observable<IPhoneRule | null> {
    return this.getAllPhoneRules().pipe(
      map(rules => rules.find(rule => rule.countryCodeDisplay === countryCode) || null)
    );
  }

  /**
   * Valida un número de teléfono sincrónicamente contra las reglas en memoria.
   * Si las reglas todavía no se han cargado retorna `{ valid: true }` para no
   * bloquear el formulario — el backend hará la validación final al enviar.
   */
  validatePhoneSync(request: IPhoneValidationRequest): IPhoneValidationResponse {
    const rules = this.rulesSignal();
    if (rules.length === 0) {
      return { valid: true, errors: [], dialingCode: '' };
    }

    const errors: string[] = [];
    const rule = rules.find(r => r.countryCodeDisplay === request.countryCode);

    if (!rule) {
      errors.push('Código de país no soportado');
      return { valid: false, errors, dialingCode: '' };
    }

    const raw = this.sanitize(request.phone);

    if (!/^\+?\d+$/.test(raw)) {
      errors.push('Número inválido. Solo dígitos y prefijo +');
    }

    let national = raw;
    if (national.startsWith('+')) {
      national = national.slice(1);
      if (national.startsWith(rule.dialingCode)) {
        national = national.slice(rule.dialingCode.length);
      }
    }

    if (national.length < rule.minLength || national.length > rule.maxLength) {
      errors.push(`Número inválido. Debe contener entre ${rule.minLength} y ${rule.maxLength} dígitos (0-9) sin espacios ni caracteres especiales.`);
    }

    const e164 = `+${rule.dialingCode}${national}`;

    return {
      valid: errors.length === 0,
      errors,
      dialingCode: rule.dialingCode,
      e164: errors.length === 0 ? e164 : undefined,
      nationalNumber: errors.length === 0 ? national : undefined,
    };
  }

  /**
   * Valida un número de teléfono (versión Observable, mantenida por compatibilidad).
   * Internamente delega en `validatePhoneSync` una vez resueltas las reglas.
   */
  validatePhone(request: IPhoneValidationRequest): Observable<IPhoneValidationResponse> {
    return this.getAllPhoneRules().pipe(
      map(rules => {
        if (this.rulesSignal().length === 0) {
          this.rulesSignal.set(rules);
        }
        return this.validatePhoneSync(request);
      })
    );
  }

  /**
   * Limpia espacios en blanco del número de teléfono
   */
  private sanitize(input: string): string {
    return input.replace(/\s+/g, '').trim();
  }

  /**
   * Limpia el caché de reglas de localStorage (útil si necesitas refrescar las reglas)
   */
  clearCache(): void {
    localStorage.removeItem(this.CACHE_KEY);
    this.rulesSignal.set([]);
    this.loadPromise = null;
  }
}
